import { schema } from 'normalizr';
import { parseDate, parseDateToDate } from 'js/utils/model';
import { FIELD_DELIMITER } from 'js/utils/string';

const parseTimestampsProcessStrategy = (value) => {
  return {
    ...value,
    inserted_at: parseDate(value.inserted_at),
    updated_at: parseDate(value.updated_at),
  };
};

const parseTimestampsAndArchivedAtProcessStrategy = (value) => {
  return {
    ...value,
    archived_at: parseDate(value.archived_at),
    inserted_at: parseDate(value.inserted_at),
    updated_at: parseDate(value.updated_at),
  };
};

const accountUserSchema = new schema.Entity(
  'accountUsers',
  {},
  { idAttribute: (value) => `${value.account_id}-${value.email}` }
);

const userSchema = new schema.Entity('users');
const accountSchema = new schema.Entity('accounts');
const workspaceSchema = new schema.Entity('workspaces', {
  account: accountSchema,
});
const workspaceUserSchema = new schema.Entity(
  'workspaceUsers',
  { user: userSchema },
  {
    idAttribute: (value) => `${value.workspace_id}-${value.email}`,
  }
);
const assessmentInviteSchema = new schema.Entity(
  'assessmentInvites',
  {},
  {
    idAttribute: (value) => `${value.assessment_id}-${value.token}`,
  }
);

const assessmentTemplateSchema = new schema.Entity(
  'assessmentTemplates',
  {},
  {
    idAttribute: (value) => `${value.assessment_template_id}-${value.revision}`,
  }
);

const assessmentDocTemplateSchema = new schema.Entity(
  'assessmentDocTemplates',
  {},
  {
    processStrategy: (value) => {
      return {
        ...value,
        archived_at: parseDateToDate(value.archived_at),
        inserted_at: parseDateToDate(value.inserted_at),
        updated_at: parseDateToDate(value.updated_at),
      };
    },
  }
);
const assessmentDocTemplateRevisionSchema = new schema.Entity(
  'assessmentDocTemplateRevisions',
  {},
  {
    processStrategy: (value) => {
      return {
        ...value,
        made_public_at: parseDateToDate(value.made_public_at),
        published_at: parseDateToDate(value.published_at),
        inserted_at: parseDateToDate(value.inserted_at),
        updated_at: parseDateToDate(value.updated_at),
      };
    },
  }
);

const assessmentDocTemplateSubscriptionSchema = new schema.Entity(
  'assessmentDocTemplateSubscriptions',
  {},
  {
    processStrategy: (value) => {
      return {
        ...value,
        made_public_at: parseDateToDate(value.made_public_at),
        published_at: parseDateToDate(value.published_at),
        inserted_at: parseDateToDate(value.inserted_at),
        updated_at: parseDateToDate(value.updated_at),
      };
    },
  }
);

const assessmentDocTemplateSummarySchema = new schema.Entity(
  'assessmentDocTemplateSummaries',
  {},
  {
    processStrategy: (value) => {
      return {
        ...value,
        doc_updated_at: parseDateToDate(value.doc_updated_at),
        latest_revision_published_at: parseDateToDate(
          value.latest_revision_published_at
        ),
        inserted_at: parseDateToDate(value.inserted_at),
        updated_at: parseDateToDate(value.updated_at),
        archived_at: parseDateToDate(value.archived_at),
      };
    },
  }
);

const meSchema = new schema.Entity('users', {
  accounts: new schema.Array(accountSchema),
  workspaces: new schema.Array(workspaceSchema),
});
const goalSchema = new schema.Entity('goals');
const teamSchema = new schema.Entity('teams', {
  goals: new schema.Array(goalSchema),
});

const rubricSchema = new schema.Entity(
  'rubrics',
  {},
  {
    processStrategy: parseTimestampsProcessStrategy,
  }
);
const favoriteTeamSchema = new schema.Entity(
  'favoriteTeams',
  {},
  { idAttribute: 'team_id' }
);

const organizationSchema = new schema.Entity('organizations');
organizationSchema.define({
  child_orgs: new schema.Array(organizationSchema),
  teams: new schema.Array(teamSchema),
});

const companySchema = new schema.Entity('companies');

const answerSchema = new schema.Entity('answers');
const responseSchema = new schema.Entity(
  'responses',
  {},
  { processStrategy: parseTimestampsProcessStrategy }
);
const questionSchema = new schema.Entity(
  'questions',
  {
    answers: new schema.Array(answerSchema),
    responses: new schema.Array(responseSchema),
  },
  {
    processStrategy: (value) => {
      return {
        ...value,
        reveal_results_on: parseDate(value.reveal_results_on),
      };
    },
  }
);
const questionSetSchema = new schema.Entity('questionSets', {
  questions: new schema.Array(questionSchema),
});
const assessmentSchema = new schema.Entity(
  'assessments',
  {
    question_sets: new schema.Array(questionSetSchema),
    participants: new schema.Array(userSchema),
  },
  { processStrategy: parseTimestampsProcessStrategy }
);
const assessmentMetaSchema = new schema.Entity('assessmentMetas');
const liveAssessmentSchema = new schema.Entity(
  'liveAssessments',
  {},
  {
    idAttribute: 'assessment_id',
  }
);
const passphraseSchema = new schema.Entity(
  'passphrases',
  {},
  {
    idAttribute: 'assessment_id',
  }
);

const productSchema = new schema.Entity('products');

const capabilitySchema = new schema.Entity(
  'capabilities',
  {},
  {
    processStrategy: parseTimestampsAndArchivedAtProcessStrategy,
  }
);

// Self-referential schema for capability tree structure
capabilitySchema.define({
  children: new schema.Array(capabilitySchema),
});
const capabilityArraySchema = new schema.Array(capabilitySchema);

const capabilitySetSchema = new schema.Entity(
  'capabilitySets',
  {
    capabilities: capabilityArraySchema,
  },
  {
    processStrategy: parseTimestampsAndArchivedAtProcessStrategy,
  }
);

const userGroupSchema = new schema.Entity(
  'userGroups',
  {},
  {
    processStrategy: parseTimestampsAndArchivedAtProcessStrategy,
  }
);

const productManagerSchema = new schema.Entity(
  'productManagers',
  {
    user: userSchema,
    user_groups: new schema.Array(userGroupSchema),
  },
  {
    processStrategy: parseTimestampsAndArchivedAtProcessStrategy,
  }
);

const favoriteUserGroupSchema = new schema.Entity(
  'favoriteUserGroups',
  {},
  {
    idAttribute: (value) => value.user_group?.id,
  }
);

const favoriteUserGroupArraySchema = new schema.Array(favoriteUserGroupSchema);

const capabilitiesAssessmentSupplementalSchema = new schema.Entity(
  'capabilitiesAssessmentSupplemental',
  {},
  {
    idAttribute: 'assessment_id',
  }
);

const userGroupAssessmentSchema = new schema.Entity(
  'userGroupAssessments',
  {
    user_group: userGroupSchema,
    assessment: assessmentSchema,
  },
  {
    processStrategy: (value) => {
      return {
        ...value,
        archived_at: parseDateToDate(value.archived_at),
        published_at: parseDateToDate(value.published_at),
        go_live_at: parseDateToDate(value.go_live_at),
        went_live_at: parseDateToDate(value.went_live_at),
        close_at: parseDateToDate(value.close_at),
        closed_at: parseDateToDate(value.closed_at),
        processed_at: parseDateToDate(value.processed_at),
        retry_processing_after: parseDateToDate(value.retry_processing_after),
        inserted_at: parseDateToDate(value.inserted_at),
        updated_at: parseDateToDate(value.updated_at),
      };
    },
  }
);

const userGroupMemberSchema = new schema.Entity(
  'userGroupMembers',
  {
    user: userSchema,
    workspace: workspaceSchema,
  },
  {
    processStrategy: parseTimestampsAndArchivedAtProcessStrategy,
  }
);

const userGroupMemberCohortSchema = new schema.Entity(
  'userGroupMemberCohorts',
  { member: userGroupMemberSchema },
  {
    processStrategy: parseTimestampsProcessStrategy,
    idAttribute: (value) =>
      `${value.user_group_id}${FIELD_DELIMITER}${value.user_group_member_id}`,
  }
);

const managedProductSchema = new schema.Entity(
  'managedProducts',
  {},
  {
    processStrategy: parseTimestampsAndArchivedAtProcessStrategy,
  }
);

const connectorSchema = new schema.Entity(
  'connectors',
  {
    workspace: workspaceSchema,
  },
  {
    processStrategy: parseTimestampsProcessStrategy,
  }
);

const teamConnectorConfigurationSchema = new schema.Entity(
  'teamConnectorConfigurations',
  {
    connector: connectorSchema,
  },
  {
    processStrategy: parseTimestampsProcessStrategy,
    idAttribute: (value) => `${value.team_id}-${value.connector_id}`,
  }
);

const connectorArraySchema = new schema.Array(connectorSchema);
const teamConnectorConfigurationArraySchema = new schema.Array(
  teamConnectorConfigurationSchema
);

const userSearchSchema = new schema.Entity(
  'userSearch',
  {},
  {
    idAttribute: 'id',
  }
);

const cycleTimeReportSchema = new schema.Entity(
  'cycleTimeReports',
  {},
  {
    idAttribute: (value) => `${value.team_id}-${value.connector_id}`,
    processStrategy: (value) => {
      return {
        ...value,
        generated_at: parseDate(value.generated_at),
        date_range: {
          ...value.date_range,
          start_date: value.date_range?.start_date
            ? parseDate(value.date_range.start_date)
            : null,
          end_date: value.date_range?.end_date
            ? parseDate(value.date_range.end_date)
            : null,
        },
      };
    },
  }
);

const userGroupStatSchema = new schema.Entity(
  'userGroupStats',
  {},
  {
    processStrategy: parseTimestampsAndArchivedAtProcessStrategy,
  }
);

function spread(entityKey, entitySchema, arraySchema) {
  arraySchema = arraySchema ?? new schema.Array(entitySchema);
  return {
    [entityKey]: entitySchema,
    [`${entityKey}_ARRAY`]: arraySchema,
  };
}

export const SCHEMAS = {
  ...spread('TEAM', teamSchema),
  ...spread('RUBRIC', rubricSchema),
  ...spread('FAVORITE_TEAM', favoriteTeamSchema),
  ...spread('GOAL', goalSchema),
  ...spread('COMPANY', companySchema),
  ...spread('ASSESSMENT', assessmentSchema),
  ...spread('ASSESSMENT_META', assessmentMetaSchema),
  ...spread('ASSESSMENT_TEMPLATE', assessmentTemplateSchema),
  ...spread('ASSESSMENT_DOC_TEMPLATE', assessmentDocTemplateSchema),
  ...spread(
    'ASSESSMENT_DOC_TEMPLATE_SUMMARY',
    assessmentDocTemplateSummarySchema
  ),
  ...spread(
    'ASSESSMENT_DOC_TEMPLATE_SUBSCRIPTION',
    assessmentDocTemplateSubscriptionSchema
  ),
  ...spread(
    'ASSESSMENT_DOC_TEMPLATE_REVISION',
    assessmentDocTemplateRevisionSchema
  ),
  ...spread('LIVE_ASSESSMENT', liveAssessmentSchema),
  ...spread('QUESTION_SET', questionSetSchema),
  ...spread('QUESTION', questionSchema),
  ...spread('ANSWER', answerSchema),
  ...spread('RESPONSE', responseSchema),
  ...spread('USER', userSchema),
  ...spread('WORKSPACE_USER', workspaceUserSchema),
  ...spread('PASSPHRASE', passphraseSchema),
  ...spread('WORKSPACE', workspaceSchema),
  ...spread('ACCOUNT', accountSchema),
  ...spread('ACCOUNT_USER', accountUserSchema),
  ...spread('ASSESSMENT_INVITES', assessmentInviteSchema),
  ...spread('ORGANIZATION', organizationSchema),
  ...spread('PRODUCT', productSchema),
  ...spread('CAPABILITY', capabilitySchema, capabilityArraySchema),
  ...spread('CAPABILITY_SET', capabilitySetSchema),
  ...spread('USER_GROUP_MEMBER', userGroupMemberSchema),
  ...spread('USER_GROUP_MEMBER_COHORT', userGroupMemberCohortSchema),
  ...spread('USER_GROUP', userGroupSchema),
  ...spread('USER_GROUP_ASSESSMENT', userGroupAssessmentSchema),
  ...spread(
    'CAPABILITIES_ASSESSMENT_SUPPLEMENTAL',
    capabilitiesAssessmentSupplementalSchema
  ),
  ...spread('PRODUCT_MANAGER', productManagerSchema),
  ...spread('MANAGED_PRODUCT', managedProductSchema),
  ...spread('CONNECTOR', connectorSchema, connectorArraySchema),
  ...spread(
    'TEAM_CONNECTOR_CONFIGURATION',
    teamConnectorConfigurationSchema,
    teamConnectorConfigurationArraySchema
  ),
  ...spread('USER_SEARCH', userSearchSchema),
  ...spread(
    'FAVORITE_USER_GROUP',
    favoriteUserGroupSchema,
    favoriteUserGroupArraySchema
  ),
  ...spread('CYCLE_TIME_REPORT', cycleTimeReportSchema),
  ...spread('USER_GROUP_STAT', userGroupStatSchema),
  ME: meSchema,
};
